<template>
  <div class="reports-container h-full flex flex-col w-full">
    <!-- Error Display -->
    <report-error
      v-if="error"
      :error="error"
      @retry="handleRetry"
      @dismiss="clearError"
    />

    <!-- Main Content (Always rendered to prevent re-renders) -->
    <div v-else class="reports-content flex-1 flex flex-col overflow-hidden w-full gap-4">
      <!-- Header with Filters -->
      <report-header
        :filter-config="filterConfig"
        :agents="agents"
        :skills="skills"
        @report-type-change="handleReportTypeChange"
        @filter-submit="handleFilterSubmit"
        @skill-changed="handleSkillChanged"
      />

      <!-- Initial Loading State -->
      <report-loading
        v-if="isLoading && !hasData"
        type="spinner"
        :message="$t('reports.loading.initial')"
      />

      <!-- Dynamic Report Component -->
      <base-report
        v-else-if="currentReportComponent && currentReportType !== 11 && currentReportType !== 13 && currentReportType !== 14 && currentReportType !== 15"
        :key="`base-report-${currentReportType}`"
        :report-data="reportData"
        :total-count="totalCount"
        :current-page="currentPage"
        :total-pages="totalPages"
        :records-per-page="recordsPerPage"
        :available-limits="availableLimits"
        :supports-pagination="supportsPagination"
        :is-loading="isLoading"
        :loading-type="currentReportConfig.loadingType"
        :loading-variant="currentReportConfig.loadingVariant"
        :loading-rows="currentReportConfig.loadingRows"
        :loading-columns="currentReportConfig.loadingColumns"
        @page-change="handlePageChange"
        @records-per-page-change="handleRecordsPerPageChange"
        @refresh="handleRefresh"
      >
        <template #table="{ data }">
          <component
            :is="currentReportComponent"
            :selected-filters="filters"
            :skills="skills"
            :agents="agents"
            :report-data="data"
            :total-count="totalCount"
            :show-count="showCount"
            :is-loading="isLoading"
          />
        </template>
      </base-report>

      <!-- CSAT Report (Special handling) -->
      <component
        v-else-if="currentReportType === 11"
        :is="currentReportComponent"
        :key="`csat-report-${currentReportType}`"
        :selected-filters="filters"
        :skills="skills"
        :report-data="reportData"
        :total-count="totalCount"
        :show-count="showCount"
        :is-loading="isLoading"
      />

      <!-- Call Reasons Report (Special handling - returns Object) -->
      <component
        v-else-if="currentReportType === 13"
        :is="currentReportComponent"
        :key="`call-reasons-report-${currentReportType}`"
        :selected-filters="filters"
        :skills="skills"
        :agents="agents"
        :report-data="reportData"
        :total-count="totalCount"
        :show-count="showCount"
        :is-loading="isLoading"
      />

      <!-- IVR Report (Special handling) -->
      <component
        v-else-if="currentReportType === 14"
        :is="currentReportComponent"
        :key="`ivr-report-${currentReportType}`"
        :selected-filters="filters"
        :skills="skills"
        :report-data="reportData"
        :total-count="totalCount"
        :show-count="showCount"
        :is-loading="isLoading"
        @exportNode="handleIVRNodeExport"
      />

      <!-- CSAT Table Report (Special handling) -->
      <component
        v-else-if="currentReportType === 15"
        :is="currentReportComponent"
        :key="`csat-table-report-${currentReportType}`"
        :selected-filters="filters"
        :report-data="reportData"
        :agents="agents"
        :is-loading="isLoading"
      />

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

// Import new components
import ReportHeader from "./components/ReportHeader.vue";
import BaseReport from "./components/BaseReport.vue";
import ReportError from "./components/ReportError.vue";
import ReportLoading from "./components/ReportLoading.vue";

// Import report components
import CallsReport from "./CallsReport.vue";
import PerformanceByPeriod from "./PerformanceByPeriod.vue";
import TotalPerformance from "./TotalPerformance.vue";
import AgentLeavingSummary from "./AgentLeavingSummary.vue";
import AgentLoginSummary from "./AgentLoginSummary.vue";
import AgentLoginDetail from "./AgentLoginDetail.vue";
import AgentStatusSummary from "./AgentStatusSummary.vue";
import AgentLeavingDetail from "./AgentLeavingDetail.vue";
import SystemInbound from "./SystemInbound.vue";
import KPIReport from "./KPIReport.vue";
import AgentStatusDetail from "./AgentStatusDetail.vue";
import CSATReport from "./CSATReport.vue";
import RepeatedCallsReport from "./RepeatedCalls.vue";
import CallReasonsReport from "./CallReasonsReport.vue";
import IVRReport from "./IVRReport.vue";
import CSATTableReport from "./CSATTableReport.vue";
import VoiceAgentCDRReport from "./VoiceAgentCDRReport.vue";
import AIChatTranscriptReport from "./AIChatTranscriptReport.vue";

// Import composables
import { useReportState } from './composables/useReportState.js';
import { useReportData } from './composables/useReportData.js';

export default {
  name: "ReportMain",
  components: {
    // New components
    ReportHeader,
    BaseReport,
    ReportError,
    ReportLoading,
    
    // Report components
    CallsReport,
    PerformanceByPeriod,
    TotalPerformance,
    AgentLeavingSummary,
    AgentLoginSummary,
    AgentLoginDetail,
    AgentStatusSummary,
    AgentLeavingDetail,
    SystemInbound,
    KPIReport,
    AgentStatusDetail,
    CSATReport,
    RepeatedCallsReport,
    CallReasonsReport,
    IVRReport,
    CSATTableReport,
    VoiceAgentCDRReport,
    AIChatTranscriptReport,
  },
  setup() {
    // Initialize state management
    const stateManager = useReportState();
    const dataManager = useReportData();
    
    return {
      stateManager,
      dataManager
    };
  },
  data() {
    return {
      agents: [],
      skills: [],
    };
  },
  computed: {
    ...mapGetters(["domainSkills", "users", "domain"]),
    
    // State from composable
    currentReportType() {
      return this.stateManager.currentReportType.value;
    },
    isLoading() {
      return this.stateManager.isLoading.value;
    },
    error() {
      return this.stateManager.error.value;
    },
    reportData() {
      return this.stateManager.reportData.value;
    },
    totalCount() {
      return this.stateManager.totalCount.value;
    },
    showCount() {
      return this.stateManager.showCount.value;
    },
    currentPage() {
      return this.stateManager.currentPage.value;
    },
    totalPages() {
      return this.stateManager.totalPages.value;
    },
    recordsPerPage() {
      return this.stateManager.recordsPerPage.value;
    },
    availableLimits() {
      return this.stateManager.availableLimits.value;
    },
    supportsPagination() {
      return this.stateManager.supportsPagination.value;
    },
    filters() {
      return this.stateManager.filters;
    },
    filterConfig() {
      return this.stateManager.filterConfig;
    },
    hasData() {
      return this.stateManager.hasData.value;
    },
    currentReportConfig() {
      return this.stateManager.currentReportConfig.value;
    },
    
    // Dynamic component mapping
    currentReportComponent() {
      return this.reportComponentMap[this.currentReportType];
    },
    
    // Report component mapping
    reportComponentMap() {
      return {
        0: 'CallsReport',
        1: 'TotalPerformance',
        2: 'PerformanceByPeriod',
        3: 'SystemInbound',
        4: 'AgentLeavingDetail',
        5: 'AgentLeavingSummary',
        6: 'AgentLoginDetail',
        7: 'AgentLoginSummary',
        8: 'AgentStatusDetail',
        9: 'AgentStatusSummary',
        10: 'KPIReport',
        11: 'CSATReport',
        12: 'RepeatedCallsReport',
        13: 'CallReasonsReport',
        14: 'IVRReport',
        15: 'CSATTableReport',
        17: 'VoiceAgentCDRReport',
        18: 'AIChatTranscriptReport'
      };
    }
  },
  methods: {
    ...mapActions([
      "GetUsers",
      "GetDomainSkills",
      "ShowProgressBar",
      "SetMessageDialog"
    ]),

    async initReportPage() {
      this.ShowProgressBar({ show: true, message: "Processing....." });
      
      try {
        // Load users
        const resp = await this.GetUsers({
          limit: 0,
          offset: 0,
          type: 0,
          status: 0,
          valid: 2,
        });
        
        if (!resp.success) {
          this.SetMessageDialog({
            success: false,
            show: true,
            message: resp.message,
          });
          return;
        }
        
        this.users.forEach((user) => {
          if(typeof user === 'object' && user !== null && user.uname) {
            this.agents.push({
              value: user.uname,
              label: user.fullname || user.uname // Use fullname if available, fallback to uname
            });
          } else if(typeof user === 'string') {
            this.agents.push({
              value: user,
              label: user
            });
          } else {
            console.warn('ReportMain: Invalid user object:', user);
          }
        });
        
        // Load skills
        const skillsResp = await this.GetDomainSkills();
        if (!skillsResp.success) {
          this.SetMessageDialog({
            success: false,
            show: true,
            message: skillsResp.message,
          });
          return;
        }
        
        this.skills = this.domainSkills;

        // Load initial report data
        await this.loadInitialData();
        
      } catch (error) {
        this.SetMessageDialog({
          success: false,
          show: true,
          message: error.message || 'Failed to initialize reports',
        });
      } finally {
        this.ShowProgressBar({ show: false, message: "" });
      }
    },

    async loadInitialData() {
      try {
        const now = moment();
        
        const defaultFilters = {
          from: now.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          to: now.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          callType: 'All',
          agent: 'All',
          hungparty: 'All',
          number: '',
          duration: '',
          skill: null,
          period: null,
          agentStatus: 'All',
          export: false
        };
        
        // First ensure filter config is up to date
        this.stateManager.resetFiltersForReportType();
        
        // Then update filters with our default values (this will preserve the date range)
        this.stateManager.updateFilters(defaultFilters);
        
        await this.loadReportData(true);
        
      } catch (error) {
        console.warn('ReportMain: Failed to load initial data:', error);
      }
    },

    async loadReportData(isNewRequest = false) {
      try {
        // Prevent multiple simultaneous requests
        if (this.isLoading) {
          return;
        }
        
        // Validate that we have a valid report type
        if (this.currentReportType === null || this.currentReportType === undefined) {
          return;
        }
        
        const payload = this.stateManager.getRequestPayload(isNewRequest);
        
        await this.dataManager.fetchReportData(this.currentReportType, payload, this.stateManager);
        
      } catch (error) {
        console.error('ReportMain: Failed to load report data:', error);
      }
    },

    // Event handlers
    async handleReportTypeChange(selected) {
      // Set the report type first (this will reset filters and update config)
      this.stateManager.setReportType(selected);
      
      // Wait a moment for the state to update
      await this.$nextTick();
      
      // Check if this report type requires skills and if we have skills loaded
      const config = this.stateManager.currentReportConfig.value;
      const updates = {};
      
      if (config.hasSkill && this.skills.length > 0 && !this.filters.skill) {
        const defaultSkill = 0;
        updates.skill = defaultSkill;
      }
      
      if (config.hasPeriod && !this.filters.period) {
        updates.period = 2;
      }
      
      if (Object.keys(updates).length > 0) {
        this.stateManager.updateFilters(updates);
      }
      
      // Load report data with the new configuration
      await this.loadReportData(true);
    },

    handleFilterSubmit(sentFilters) {
      if (sentFilters) {
        this.stateManager.updateFilters(sentFilters);
        this.loadReportData(true);
      }
    },

    handleSkillChanged(skill) {
      this.stateManager.updateFilters({ skill });
    },

    handlePageChange(page) {
      this.stateManager.setPage(page);
      this.loadReportData(false);
    },

    handleRecordsPerPageChange(limit) {
      this.stateManager.setRecordsPerPage(limit);
      this.loadReportData(true);
    },

    handleResetFilters() {
      this.stateManager.resetState();
      this.loadInitialData();
    },

    handleRetry() {
      this.loadReportData(true);
    },

    handleRefresh() {
      // Optimized refresh that doesn't cause full component re-render
      if (this.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      // Clear any existing error first
      this.stateManager.clearError();
      
      // Use a more targeted refresh approach
      this.refreshCurrentData();
    },
    
    async refreshCurrentData() {
      try {
        // Only refresh if we have a current report type
        if (this.currentReportType === null || this.currentReportType === undefined) {
          return;
        }
        
        const payload = this.stateManager.getRequestPayload(true);
        await this.dataManager.fetchReportData(this.currentReportType, payload, this.stateManager);
      } catch (error) {
        console.error('Failed to refresh data:', error);
      }
    },

    clearError() {
      this.stateManager.clearError();
    },

    async handleIVRNodeExport(exportData) {
      // Handle IVR node export - re-fetch report with export: true and node id
      try {
        const payload = this.stateManager.getRequestPayload(true);
        
        // Add export parameters (store expects 'export' not 'isexport')
        payload.export = exportData.isexport;
        payload.node = exportData.node;
        
        await this.dataManager.fetchReportData(this.currentReportType, payload, this.stateManager);
      } catch (error) {
        console.error('Failed to export IVR node:', error);
      }
    },
  },
  
  watch: {
    async domain(newValue) {
      if (newValue) {
        await this.initReportPage();
      }
    },
  },
  
  async mounted() {
    if (this.domain) {
      await this.initReportPage();
    }
  },
  
};
</script>

<style scoped>
/* Ensure full width utilization */
.reports-container {
  width: 100%;
  max-width: 100%;
}

.reports-content {
  width: 100%;
  max-width: 100%;
}

/* Ensure all child elements take full width */
.reports-container > *,
.reports-content > * {
  width: 100%;
  max-width: 100%;
}
</style>
